/* eslint-disable react-hooks/exhaustive-deps */ // Needed to help reduce rerenders for epgItem
/* eslint-disable jsx-a11y/no-static-element-interactions, react/no-array-index-key */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// import Badge from 'components/Badge'; // Need to know what styles to pass
import _ from 'lodash';
import classNames from 'classnames';
import { truncateText } from 'helpers/ContentHelper';

import { isMobileLandscape } from 'helpers/BreakpointHelper';

import {
  FORMAT_TIME,
  now,
  differenceInMinutes,
  differenceInSeconds,
  isBefore,
  isAfter,
  isSameSecond,
  format,
  stringToDate,
  getHours,
  getMinutes,
  isSameDay,
  msToDate,
} from 'helpers/DateTimeHelper';

import {
  calculateTimeTil,
  calculateWidthByDuration,
  formatTimeLeft,
  getVideoData,
  getVideoTimeData,
  isGapFiller,
  isVideoLive,
  MINUTES_INTERVAL,
} from 'screens/Live/LiveHelpers';

import useCurrentPositionAndTime from 'hooks/useCurrentPositionAndTime';

import Style from './EPGItem.module.scss';

const TYPE_CURRENT = 'current';
const TYPE_FUTURE = 'future';
const TYPE_DUMMY = 'dummy';
const TYPE_OVERLAP = 'overlap';

const EPGItem = props => {
  const {
    breakpoint,
    ended,
    endingDate,
    isOverFlow,
    lateProgramIdx,
    overFlowProgramIdx,
    selectedDate,
    selectedVideoData,
    startingTs,
    totalMinutes,
  } = props;
  const { currentPosition } = useCurrentPositionAndTime({
    totalMinutes,
    startingTs,
    ended,
  });
  const [startingDate] = useState(now());
  const [renderedItems, setRenderItem] = useState(null);
  const mobileGridHours = 72;
  const GridHours = 8;

  useEffect(() => {
    const calculateBlockWidth = (type, videoTimeData, previousTimeData) => {
      const gridStarts = msToDate(startingTs * 1000);
      let timeLeft;
      let width;

      const { duration, startDate, endDate } = videoTimeData;
      const starts = stringToDate(startDate);
      const ends = stringToDate(endDate);
      const runsOver = isAfter(ends, endingDate);

      let prevEnd = '';
      if (previousTimeData) {
        const { endDate: prevEndDate } = previousTimeData;
        prevEnd = stringToDate(prevEndDate);
      }

      switch (type) {
        case TYPE_FUTURE:
          width = calculateWidthByDuration(
            runsOver ? differenceInMinutes(endingDate, startDate) : duration,
          );
          break;
        case TYPE_CURRENT:
          timeLeft = differenceInMinutes(runsOver ? endingDate : ends, gridStarts);
          width = calculateWidthByDuration(timeLeft);
          break;
        case TYPE_DUMMY:
          timeLeft = differenceInMinutes(starts, gridStarts);
          width = calculateWidthByDuration(timeLeft);
          break;
        case TYPE_OVERLAP:
          width = calculateWidthByDuration(
            runsOver
              ? differenceInMinutes(endingDate, startDate)
              : differenceInMinutes(ends, prevEnd),
          );
          break;
        default:
          width = 0;
          break;
      }

      return width;
    };

    const addDummyBlocks = () => {
      const { item, selectedDate, breakpointIsMobile } = props;
      const gridTotalHours = breakpointIsMobile ? mobileGridHours : GridHours;
      const canScrollGrid = true;
      const member = _.get(item, 'items.member', []);

      const blocks = [];
      let currentShown = false;
      member.map((vid, idx) => {
        let video = vid;

        // This corrects start dates for overflow channels
        if (!isOverFlow && overFlowProgramIdx !== -1 && idx > lateProgramIdx) {
          const lateProgramEndDateString = member[lateProgramIdx]?.endDate;
          const videoStartDate = stringToDate(video.startDate);
          const videoEndDate = stringToDate(video.endDate);
          const lateProgramEndDate = stringToDate(lateProgramEndDateString);

          const isBeforeLateProgram = isBefore(videoStartDate, lateProgramEndDate);
          const isAfterLateProgram = isAfter(videoEndDate, lateProgramEndDate);

          if (!isAfterLateProgram) return null;
          if (isBeforeLateProgram) {
            video = _.cloneDeep(video);
            video.startDate = lateProgramEndDateString;
          }
        }
        const videoTimeData = getVideoTimeData({
          video,
          current: selectedDate,
          gridTotalHours: isMobileLandscape() ? mobileGridHours : gridTotalHours,
          canScrollGrid,
        });
        const { description, id, title, seasonAndEpisode, subtitle, badge } = getVideoData(
          video,
          true,
        );
        const {
          endDate,
          ends,
          isCurrent,
          shouldBeVisible,
          startDate,
          starts,
          startsInFuture,
          startsWithGrid,
          withinSameDay,
        } = videoTimeData;
        const videoIsGapFiller = isGapFiller(video);
        let width = null;
        if (!isCurrent && !startsInFuture) {
          return null;
        }
        // if starts in future, calculate the width using duration
        if (startsInFuture && !isCurrent) {
          width = calculateBlockWidth(TYPE_FUTURE, videoTimeData);
          // if its current, calculate the timeLeft until it ends.
        } else if (isCurrent) {
          width = calculateBlockWidth(TYPE_CURRENT, videoTimeData);
          currentShown = true;
        }

        // add the dummy block if is there a gap
        if (startsInFuture && !currentShown) {
          const ends = format(stringToDate(video.startDate), FORMAT_TIME);
          blocks.push({
            badge,
            description: '',
            endDate,
            ends,
            id: `dummy-${id}`,
            isDummy: true,
            itemClass: classNames(Style.epgItem, Style.current),
            seasonAndEpisode: '',
            shouldBeVisible: true,
            startDate,
            starts: '',
            subtitle: '',
            title: '',
            video,
            width: calculateBlockWidth(TYPE_DUMMY, videoTimeData),
          });
          currentShown = true;
        }
        if (shouldBeVisible || withinSameDay) {
          const previousBlock = blocks.length !== 0 ? blocks.slice(-1)[0] : {};
          const { startDate: prevStartDate, endDate: prevEndDate } = previousBlock;
          const cellAfterPrev =
            isAfter(startDate, prevStartDate) || isSameSecond(prevStartDate, startDate);
          const cellStartsBeforePrevEnds =
            isBefore(prevEndDate, startDate) || isSameSecond(prevEndDate, startDate);
          const cellEndsAfterPrevEnds = isAfter(endDate, prevEndDate);
          const cellEndsAtGridEnd =
            isAfter(endDate, endingDate) || isSameSecond(endDate, endingDate);
          if (cellAfterPrev && !cellStartsBeforePrevEnds && !isCurrent) {
            // means next cell is contained within previous cell if next cell is not longer than previous do nothing
            if (cellEndsAfterPrevEnds) {
              const { video: prevVideo } = previousBlock;
              const previousTimeData = getVideoTimeData({
                video: prevVideo,
                current: selectedDate,
                gridTotalHours: isMobileLandscape() ? mobileGridHours : gridTotalHours,
                canScrollGrid,
              });
              // this means a sliver of the newest must be shown because that cell ends after previous
              if (!cellEndsAtGridEnd) {
                blocks.push({
                  badge,
                  description,
                  endDate,
                  ends,
                  id,
                  isCurrent,
                  isDummy: videoIsGapFiller,
                  seasonAndEpisode,
                  shouldBeVisible,
                  startDate,
                  starts,
                  startsWithGrid,
                  subtitle,
                  title,
                  video,
                  width: calculateBlockWidth(TYPE_OVERLAP, videoTimeData, previousTimeData),
                });
              }
            }
          } else {
            blocks.push({
              badge,
              description,
              endDate,
              ends,
              id,
              isCurrent,
              isDummy: videoIsGapFiller,
              seasonAndEpisode,
              shouldBeVisible,
              startDate,
              starts,
              startsWithGrid,
              subtitle,
              title,
              video,
              width,
            });
          }
        }
        return null;
      });

      if (blocks.length === 0) {
        const selectedTime = selectedDate;
        if (selectedTime.getMinutes() < 30) {
          selectedTime.setMinutes(0);
        } else if (selectedTime.getMinutes() > 30) {
          selectedTime.setMinutes(30);
        }
        blocks.push({
          badge: null,
          description: '',
          ends: '',
          id: 'dummy-1',
          isDummy: true,
          itemClass: classNames(Style.epgItem, Style.current),
          seasonAndEpisode: '',
          shouldBeVisible: true,
          startDate: '',
          starts: '',
          subtitle: '',
          title: '',
          width: calculateWidthByDuration(differenceInMinutes(endingDate, selectedTime)),
        });
      } else {
        // add a dummy at the end of a row
        const { endDate: lastBlockEndDate } = blocks.slice(-1)[0] || {};
        const isBeforeLast = isBefore(lastBlockEndDate, endingDate);
        if (isBeforeLast) {
          blocks.push({
            badge: null,
            description: '',
            ends: '',
            id: 'dummy-end',
            isDummy: true,
            itemClass: classNames(Style.epgItem, Style.current),
            seasonAndEpisode: '',
            shouldBeVisible: true,
            startDate: '',
            starts: '',
            subtitle: '',
            title: '',
            width: calculateWidthByDuration(differenceInMinutes(endingDate, lastBlockEndDate)),
          });
        }
      }
      // Adding 6 hours to finish EPG for Tablet and Greater
      if (!breakpointIsMobile) {
        blocks.push({
          badge: null,
          description: '',
          ends: '',
          id: 'dummy-1',
          isDummy: true,
          itemClass: classNames(Style.epgItem, Style.current),
          seasonAndEpisode: '',
          shouldBeVisible: true,
          startDate: '',
          starts: '',
          subtitle: '',
          title: '',
          width: calculateWidthByDuration(360),
        });
      }
      return blocks;
    };

    const checkAtStarting = date => {
      const startingHours = getHours(startingDate);
      const startingMinutes = getMinutes(startingDate);
      const startingTime = startingHours * 60 + Math.floor(startingMinutes / MINUTES_INTERVAL);

      const dateHours = getHours(date);
      const dateMinutes = getMinutes(startingDate);
      const time = dateHours * 60 + Math.floor(dateMinutes / MINUTES_INTERVAL);

      return isSameDay(startingDate, date) && startingTime >= time;
    };

    const renderItems = () => {
      const atStartDate = checkAtStarting(selectedDate);
      const blocks = addDummyBlocks();

      if (!blocks) return null;

      return blocks.map((block, idx) => {
        const {
          description,
          endDate,
          id,
          isDummy,
          seasonAndEpisode,
          startDate,
          starts,
          title,
          video,
          width,
        } = block;

        const isLive = isVideoLive(video);

        let itemClass =
          selectedVideoData && selectedVideoData['@id'] === id
            ? classNames(Style.epgItem, Style.current)
            : Style.epgItem;

        itemClass = isDummy ? classNames(itemClass, Style.dummy) : itemClass;

        const itemStyle = {
          left: `${currentPosition}%`,
          width: `${width}px`,
        };

        const progressStyle = {
          background: `-webkit-gradient(linear, left top, right top, color-stop(${currentPosition}%, rgba(255, 255, 255, 0.1)), color-stop(${currentPosition}%, transparent))`,
        };
        const currentTime = new Date();

        let timeLeft = '';
        let timeTil = '';
        if (isLive) {
          const dateEnd = stringToDate(endDate);
          const runsOver = isAfter(dateEnd, endingDate);
          const secondsLeft = differenceInSeconds(runsOver ? endingDate : dateEnd, currentTime);
          timeLeft = formatTimeLeft(secondsLeft);
        } else {
          timeTil = calculateTimeTil(currentTime, startDate, starts);
        }

        let chars;
        switch (width) {
          case 252:
            chars = 30;
            break;
          case 514:
            chars = 60;
            break;
          default:
            chars = 120;
            break;
        }
        const trancatedTitle = truncateText(title, chars);

        const subtitleSeason = seasonAndEpisode && <span>{seasonAndEpisode} &#183;</span>;

        return (
          <div key={idx} className={itemClass} style={itemStyle} data-test="scheduleitem-item">
            <div style={progressStyle} className={atStartDate ? Style.progress : null} />
            <div className={Style.timeContainer}>
              <span className={Style.timeMeta}>{isLive ? timeLeft : starts}</span>
              <span className={Style.timeMetaHovered}>
                {isLive ? `started at ${starts}` : timeTil}
              </span>
            </div>
            <div className={Style.titleContainer}>
              <span className={Style.title}>
                <span className={Style.titleText}>
                  {trancatedTitle} {seasonAndEpisode && ` - ${seasonAndEpisode}`}
                </span>
              </span>
            </div>
            <div className={Style.subtitleContainer}>
              <span className={Style.subtitle}>
                {subtitleSeason} {description}
              </span>
            </div>
          </div>
        );
      });
    };
    setRenderItem(renderItems());
  }, [selectedDate, endingDate, breakpoint, currentPosition]);
  return (
    <div className={Style.scheduleMain}>
      <div className={Style.container}>{renderedItems}</div>
    </div>
  );
};

EPGItem.defaultProps = {
  account: {},
  favoritesManager: null,
  location: {},
  router: {},
  selectedDate: {},
  selectedVideoData: null,
};

EPGItem.propTypes = {
  account: PropTypes.shape({}),
  breakpoint: PropTypes.string.isRequired,
  ended: PropTypes.func,
  endingDate: PropTypes.instanceOf(Date),
  favoritesManager: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})),
    setFavorites: PropTypes.func,
    deleteFavorites: PropTypes.func,
  }),
  item: PropTypes.shape({
    autoPlayStill: PropTypes.shape({}),
    autoPlayVideo: PropTypes.shape({}),
    cacheSetting: PropTypes.string,
    callSign: PropTypes.string,
    dateModified: PropTypes.string,
    headline: PropTypes.string,
    hideIfLoggedIn: PropTypes.bool,
    hideIfNotLoggedIn: PropTypes.bool,
    id: PropTypes.string,
    imageTypes: PropTypes.arrayOf(PropTypes.string),
    images: PropTypes.shape({}),
    items: PropTypes.shape({}),
    itemsPerPage: PropTypes.number,
    name: PropTypes.string,
    panelType: PropTypes.string,
    validFor: PropTypes.number,
  }).isRequired,
  isOverFlow: PropTypes.bool.isRequired,
  lateProgramIdx: PropTypes.number.isRequired,
  location: PropTypes.shape({}),
  overFlowProgramIdx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
  selectedDate: PropTypes.shape({}),
  selectedVideoData: PropTypes.shape({}),
  startingTs: PropTypes.number.isRequired,
  totalMinutes: PropTypes.number.isRequired,
};

export default React.memo(EPGItem);
